import { useTranslation } from 'react-i18next';

import CancelIcon from '@crm/shared/assets/icons/cancel-01.svg?react';
import { cn, DateHelpers } from '@crm/shared/lib';
import { Badge, Button, DatePicker, SvgIcon } from '@crm/shared/ui';

import { formatDealDateBadge, getDealDateBadgeClassName } from '../../lib';
import { DealTableCell } from '../DealTableCell';

interface DealDatePickerCellProps {
    changeDateAriaLabel?: string;
    clearDateAriaLabel?: string;
    date?: string | null;
    emptyText?: string;
    isDisabled?: boolean;
    isPending?: boolean;
    onChange: (date?: string) => void;
}

export const DealDatePickerCell = ({
    changeDateAriaLabel,
    clearDateAriaLabel,
    date,
    emptyText,
    isDisabled = false,
    isPending = false,
    onChange,
}: DealDatePickerCellProps) => {
    const { t } = useTranslation();
    const resolvedChangeDateAriaLabel =
        changeDateAriaLabel ?? t('crm.common.dealsTable.cells.date.changeAriaLabel');
    const resolvedClearDateAriaLabel =
        clearDateAriaLabel ?? t('crm.common.dealsTable.cells.date.clearAriaLabel');
    const resolvedEmptyText = emptyText ?? t('crm.common.dealsTable.cells.date.empty');

    return (
        <DealTableCell
            isEmpty={false}
            className="px-1"
            onClick={(event) => event.stopPropagation()}
        >
            <div className="flex items-center justify-center">
                <DatePicker
                    align="center"
                    value={date ?? undefined}
                    disabled={isDisabled || isPending}
                    hideLabel
                    parseDate={DateHelpers.parseApiDate}
                    formatDate={(selectedDate: Date | undefined) =>
                        selectedDate ? DateHelpers.formatApiDate(selectedDate) : ''
                    }
                    onChange={onChange}
                    trigger={
                        date ? (
                            <Badge
                                render={
                                    <button
                                        type="button"
                                        aria-label={resolvedChangeDateAriaLabel}
                                    />
                                }
                                className={cn(
                                    'group h-6 max-w-full min-w-22.5 cursor-pointer rounded-xl py-1 text-xs font-normal transition-opacity duration-200 hover:opacity-70',
                                    getDealDateBadgeClassName(date)
                                )}
                            >
                                <span className="leading-none">
                                    {formatDealDateBadge(date, t('crm.common.date.today'))}
                                </span>
                                <span
                                    className="hidden items-center group-data-[popup-open]:flex"
                                    aria-label={resolvedClearDateAriaLabel}
                                    onPointerDown={(event) => {
                                        event.preventDefault();
                                    }}
                                    onClick={() => {
                                        onChange(undefined);
                                    }}
                                >
                                    <SvgIcon
                                        icon={CancelIcon}
                                        size="16"
                                        className="block -translate-y-px"
                                    />
                                </span>
                            </Badge>
                        ) : (
                            <Button
                                className="h-6 text-smtr-main-accent-100 hover:text-smtr-main-accent-200 hover:no-underline"
                                variant="link"
                            >
                                {resolvedEmptyText}
                            </Button>
                        )
                    }
                />
            </div>
        </DealTableCell>
    );
};
